import './styles.scss'

import {
  Box,
  CircularProgress,
  Grid,
  Typography,
  useTheme
} from '@mui/material'
import Analytics, { Events } from 'analytics/Analytics'
import { ArrowRight, logo } from 'assets/icons'
import { AxiosError } from 'axios'
import Calendar from 'components/calendar/Calendar'
import { calculateListViewRange } from 'components/calendar/ListView'
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog'
import EmailHighlight from 'components/dialogs/embed/EmailHighlight'
import { processLogout } from 'components/providers/AuthProvider'
import { useCalendar } from 'components/providers/CalendarProvider'
import { doGetChanges } from 'components/providers/GetChangesProvider'
import { useNotification } from 'components/providers/NotificationProvider'
import dayjs from 'dayjs'
import i18n from 'i18n'
import { t } from 'i18next'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getEventsForGroup } from 'services/api/event'
import Database from 'services/db/Database'
import LocalStorage from 'services/LocalStorage'
import Logger from 'services/Logger'
import { GroupDoc } from 'types/api/changes'
import {
  CalendarRange,
  getCalendarRangeByString
} from 'types/components/calendar'
import { LocalStorageKey } from 'types/services/localStorage'
import useFitText from 'use-fit-text'
import { MULTIPLE_GROUPS_EMBED_WITHOUT_TIER } from 'utils/api/codes'
import { APP_LINK, isMobileDevice } from 'utils/constants'
import { extractDeepLinkCode } from 'utils/deeplink'
import { appStoreLinkGroupcal, getMobileOS } from 'utils/device'
import { isInIframe, isTestEnv } from 'utils/env'
import { isPreviewMode } from 'utils/user'

import GuestHeader from './GuestHeader'

export interface GuestModeProps {
  showHeader?: string
  defaultView?: CalendarRange
  groupId: string
  width?: string
  height?: string
  preview?: boolean
  groupsIds?: string[]
  calendarTitle?: string
}

export default function GuestMode(props: GuestModeProps) {
  const theme = useTheme()
  const calendar = useCalendar()

  const [group, onGroup] = useState<GroupDoc | undefined>(undefined)
  const [showNotValidCalendar, setShowNotValidCalendar] = useState(false)

  useEffect(() => {
    if (group) {
      const inviteCode = extractDeepLinkCode(group?.PrivateLinkUrl ?? '') // set this dynamically
      if (getMobileOS() === 'ios') {
        if (inviteCode) {
          // Replace 'yourappscheme://' with your actual URL scheme
          const schemeUrl = `GroupCal24me://invite_code=${inviteCode}`
          const iframe = document.createElement('iframe')
          iframe.style.display = 'none'
          iframe.src = schemeUrl
          document.body.appendChild(iframe)

          // Optionally, fallback to App Store after a timeout
          setTimeout(() => {
            document.body.removeChild(iframe)
          }, 2000)
        } else {
        }
      }
    }
  }, [group])

  const isEmbed = props.preview

  useEffect(() => {
    Analytics.getInstance().sendEvent(Events.GEUST_MODE_OPEN)
  }, [])

  const { showNotification } = useNotification()
  const queryParams = new URLSearchParams(location.search)
  const showHeader = queryParams.get('showHeader')
  const isPreview = isPreviewMode()
  const hidePoweredBy = queryParams.get('removeBranding') === '1'
  const queryView = getCalendarRangeByString(queryParams.get('view') ?? '')
  const preferredGuestView =
    queryView ??
    getCalendarRangeByString(
      LocalStorage.get(LocalStorageKey.CALENDAR_VIEW) ?? ''
    ) ??
    CalendarRange.DAY
  const [canHideBranding, setCanHideBranding] = useState<boolean | undefined>(
    undefined
  )
  const [canUseCardsView, setCanUseCardsView] = useState<boolean | undefined>(
    preferredGuestView === CalendarRange.CARDS ? true : undefined
  )
  const [isEventsLoading, setIsEventsLoading] = useState(
    Boolean(props.groupId || (props.groupsIds?.length ?? 0) > 0)
  )
  const initialViewAppliedRef = useRef(false)
  const handledBlockedEmbedRef = useRef(false)
  const loadingEventsRef = useRef(false)
  const lastFetchedRangeKeyRef = useRef<string | null>(null)
  // Show branding by default unless both URL flag and server capability explicitly allow hiding it.
  const shouldShowBranding = !(hidePoweredBy && canHideBranding === true)

  useEffect(() => {
    if (initialViewAppliedRef.current) return
    initialViewAppliedRef.current = true

    Logger.blue(
      'GuestMode:',
      'Setting initial calendar view to',
      preferredGuestView
    )
    calendar.onView(preferredGuestView)
  }, [calendar, preferredGuestView])

  useEffect(() => {
    if (
      canUseCardsView !== false ||
      preferredGuestView !== CalendarRange.CARDS ||
      calendar.view !== CalendarRange.CARDS
    ) {
      return
    }

    calendar.onView(CalendarRange.WEEK)
    showNotification(t('calendarCannotDisplayed'), 'error', 'bottom', null)
  }, [canUseCardsView, preferredGuestView, calendar, showNotification])

  const fetchGuestEventsByRange = useCallback(
    async (
      dateRange: { leftBound: number; rightBound: number },
      view: CalendarRange
    ) => {
      if (!(props.groupId || (props.groupsIds?.length ?? 0) > 0)) return

      let start = dateRange.leftBound
      let end = dateRange.rightBound

      // Keep guest list fetch behavior aligned with CalendarView.
      if (view === CalendarRange.LISTWEEK || view === CalendarRange.LISTDAY) {
        const customRange = calculateListViewRange(
          view,
          dayjs.unix(dateRange.leftBound).toISOString()
        )
        start = customRange.start.unix()
        end = customRange.end.unix()
      }

      const rangeKey = `${props.groupId}|${(props.groupsIds ?? []).join(',')}|${start}|${end}`

      // FullCalendar emits extra range updates during initialization; ignore duplicates.
      if (
        loadingEventsRef.current ||
        lastFetchedRangeKeyRef.current === rangeKey
      ) {
        return
      }

      loadingEventsRef.current = true
      setIsEventsLoading(true)

      try {
        const eventsAndGroupData = await getEventsForGroup(
          props.groupId,
          start,
          end,
          props.groupsIds
        )

        handledBlockedEmbedRef.current = false
        lastFetchedRangeKeyRef.current = rangeKey

        if (!isPreview) onGroup(eventsAndGroupData.groups[0])

        setCanHideBranding(eventsAndGroupData.canRemovePoweredBy)
        setCanUseCardsView(eventsAndGroupData.canUseCardsView || false)

        calendar.onGroupServer(
          (props.groupsIds?.length ?? 0) > 0
            ? ''
            : (eventsAndGroupData.groups[0]?._id ?? '')
        )
      } catch (error) {
        if (error instanceof AxiosError) {
          const httpCode = error.response?.status

          if (httpCode === 403) {
            const code = (error.response?.data as unknown as any)['code']

            if (code === MULTIPLE_GROUPS_EMBED_WITHOUT_TIER) {
              if (handledBlockedEmbedRef.current) return

              handledBlockedEmbedRef.current = true

              if (view !== CalendarRange.WEEK) {
                calendar.onView(CalendarRange.WEEK)
              }

              setShowNotValidCalendar(true)
            }
          }
        }
      } finally {
        loadingEventsRef.current = false
        setIsEventsLoading(false)
      }
    },
    [props.groupId, props.groupsIds, calendar, isPreview, showNotification]
  )

  return (
    <Box
      bgcolor={theme.palette.mode === 'dark' ? '#5a5a5a' : '#EFF2F4'}
      display="flex"
      className="guest-root"
      position={'relative'}
      height={props.height ? props.height : '100dvh'}
      width={props.width ? props.width : '100dvw'}
    >
      {isEventsLoading && (
        <Box
          position={'absolute'}
          top={0}
          right={0}
          bottom={0}
          left={0}
          zIndex={1000}
          display={'flex'}
          alignItems={'center'}
          justifyContent={'center'}
          sx={{ pointerEvents: 'none' }}
        >
          <CircularProgress size={36} />
        </Box>
      )}
      <Box
        bgcolor={
          isEmbed || props.preview || isInIframe()
            ? 'transparent'
            : 'var(--color-surface-canvas)'
        }
        display="flex"
        flex={1}
        padding={isMobileDevice() ? '2px 16px 16px 16px' : 4}
        minWidth={'100px'}
        minHeight={'100px'}
        flexDirection={{ xs: 'column', sm: 'column' }}
      >
        {(showHeader ?? props.showHeader) === '0' ? (
          <></>
        ) : (
          <GuestHeader group={group} />
        )}
        <Box height={'100%'}>
          <Calendar
            canUseCardsView={canUseCardsView}
            calendarTitle={props.calendarTitle}
            groupId={props.groupId}
            groupIds={props.groupsIds}
            readOnly
            embedMode={props.preview}
            onGuestDateRangeChange={fetchGuestEventsByRange}
          />
        </Box>
      </Box>
      <Box
        id={'groupcal-branding-button'}
        position={'absolute'}
        zIndex={99999}
        display={shouldShowBranding ? 'flex' : 'none'}
        bottom={32}
        right={i18n.dir() === 'ltr' ? 32 : undefined}
        left={i18n.dir() === 'rtl' ? 32 : undefined}
        sx={{
          borderRadius: '4px',
          backgroundColor:
            'color-mix(in srgb, var(--color-surface-card) 82%, transparent)',
          boxShadow: '0px 1px 8px rgba(0, 0, 0, 0.1)',
          border: `1px solid ${theme.palette.border.primary}`
        }}
      >
        <Grid
          item
          component="a"
          href="https://www.groupcal.app"
          target="_blank"
          rel="noopener"
          wrap={'nowrap'}
          direction={'row'}
          display={'flex'}
          padding={theme.spacing(0.5, 2)}
          alignItems={'center'}
          gap={1}
          sx={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
        >
          <img
            src={logo}
            style={{ width: 18, height: 18, cursor: 'pointer' }}
          />
          <Typography
            fontFamily={'NotoSans'}
            fontSize={'14px'}
            fontWeight={400}
          >
            GroupCal
          </Typography>
        </Grid>
      </Box>
      {showNotValidCalendar && (
        <ConfirmationDialog
          leftButtonHidden
          rightButton={t('close')}
          handleRightButton={() => {
            setShowNotValidCalendar(false)
          }}
          title={t('notValidCalendar')}
          content={<EmailHighlight text={t('notValidCalendarDesc')} />}
          open
        />
      )}
    </Box>
  )
}
export interface JoinGroupButtonProps {
  title?: string
  group?: GroupDoc
}

export function JoinGroupButton(props: JoinGroupButtonProps) {
  const { fontSize, ref } = useFitText({
    minFontSize: 50,
    maxFontSize: 100
  })
  const navigate = useNavigate()
  const goToLogin = () => {
    Analytics.getInstance().sendEvent(Events.GEUST_MODE_OPEN_FULL_VERSION)
    const inviteCode = extractDeepLinkCode(props.group?.PrivateLinkUrl ?? '') // set this dynamically

    Database.clear()
      .then(() => {
        if (getMobileOS() === 'android') {
          if (inviteCode) {
            const playStoreUrl = `https://play.google.com/store/apps/details?id=app.groupcal.www${isTestEnv() ? '.debug' : ''}&referrer=invite_code%3D${encodeURIComponent(inviteCode)}`
            window.open(playStoreUrl, '_self')
          } else {
            // Open new page in the same tab with mobile link
            window.location.href = `${APP_LINK}/${LocalStorage.get(
              LocalStorageKey.MOBILE_LINK
            )}`
          }
        } else if (getMobileOS() === 'ios') {
          navigator.clipboard.writeText(`Deeplink=${inviteCode}`)
          window.open(appStoreLinkGroupcal, '_blank')

          if (!inviteCode) {
            window.location.href = `${APP_LINK}/${LocalStorage.get(
              LocalStorageKey.MOBILE_LINK
            )}`
          }
        } else {
          navigate('/')
          LocalStorage.remove(LocalStorageKey.LAST_UPDATE)
          LocalStorage.remove(LocalStorageKey.CLEAN_GET_CHANGES_DONE)
          processLogout().then(() => {
            doGetChanges(null, undefined, null)
          })
        }
      })
      .catch((error) => {
        Logger.red('GuestMode', 'Error clearing database', error)
      })
  }

  if (isMobileDevice()) {
    return (
      <div
        onClick={goToLogin}
        id="joinGroupButton"
        style={{
          flexShrink: 0, // never shrink
          flexGrow: 0, // only take needed space
          backgroundColor: 'var(--color-surface-card)',
          border: '1px solid var(--color-border-default)',
          borderRadius: '68px',
          padding: '8px 8px',
          height: '40px'
        }}
      >
        {t('joinCalendarTitle')}
      </div>
    )
  }

  return (
    <div
      ref={ref}
      style={{
        height: isMobileDevice() ? '30px' : '50px',
        fontSize: fontSize,
        width: '100%'
      }}
      className="goToSignUp"
      onClick={goToLogin}
      id="joinGroupButton"
    >
      {props.title ?? t('joinCalnedar')}
      <ArrowRight className="arrow" style={{ marginLeft: 8 }} />
    </div>
  )
}
